import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { FeaturedVideo } from '@/shared/types';
import { BookmarkFilledIcon, BookmarkIcon, Button, PauseIcon, PlayIcon, ValoiV } from '@/shared/ui';
import { safePlay } from '@/shared/utils/media';

function formatRuntime(seconds?: number): string | null {
  if (!seconds || seconds <= 0) return null;
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}

export type NowShowingHeroProps = {
  video: FeaturedVideo;
  saved: boolean;
  onToggleSave: (video: FeaturedVideo) => void;
  /** True once the viewer has explicitly chosen a card, so playback may start. */
  autoplay: boolean;
  /** The stage itself, so choosing a card further down the page can bring it back. */
  anchorRef?: RefObject<HTMLElement | null>;
  /**
   * Preview on pointer hover, stopping on leave. Off by default: on Home the
   * viewer has already chosen this video and a preview that dies on mouse-out
   * would interrupt it. The public page opts in — there, hovering IS the
   * browsing gesture.
   *
   * Pressing Watch now still wins. Once committed, leaving the stage does not
   * stop playback, because "play until I stop it" and "play while I point at
   * it" are different promises and the explicit one outranks the incidental.
   */
  previewOnHover?: boolean;
  /** Called when the clip plays to its end, for a caller that advances a reel. */
  onEnded?: () => void;
  /**
   * Called when playback stops without finishing — Pause pressed, or the pointer
   * leaving a hover preview. A caller running a reel uses this to start waiting
   * again, rather than advancing immediately: the viewer stopped on this video,
   * so it stays on screen until the idle delay says otherwise.
   */
  onPause?: () => void;
  /** Called when playback starts, so a caller running a reel can hold its place. */
  onPlay?: () => void;
};

/**
 * The `NOW SHOWING` hero: copy on the left, artwork bleeding off the right edge
 * behind a feathered mask.
 *
 * Pressing Watch now turns that same artwork region into the player rather than
 * revealing a second one below — the design has one stage, and a full-width
 * player stacked under the hero pushes the rails off the fold. Nothing plays
 * until asked, so arriving on the page is silent.
 */
export function NowShowingHero({
  video,
  saved,
  onToggleSave,
  autoplay,
  anchorRef,
  previewOnHover = false,
  onEnded,
  onPause,
  onPlay,
}: NowShowingHeroProps) {
  /* Two independent reasons the video may be running, kept apart on purpose.
     `committed` is a decision (Watch now, or a card chosen further down the
     page) and survives the pointer leaving; `previewing` is incidental and dies
     with it. Collapsing them into one boolean is what made mouse-out kill a
     video the viewer had explicitly started. */
  const [committed, setCommitted] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const playing = committed || previewing;
  /* Whether the ELEMENT is running, which is not the same as `committed`.
     `committed` is the decision to show a player at all; this is what that player
     is doing. Pausing from the native controls leaves the decision intact -- the
     player should stay on screen -- but the button was labelled from the decision,
     so it read "Pause" over an already-paused video. Driven by the element's own
     play/pause events, so it is right whichever control was used. */
  const [running, setRunning] = useState(false);

  /* An unmounting <video> fires no `pause` event, so `running` has to be cleared
     here as well as from the element's own handler. Hovering out ends the preview,
     which drops `playing` to false and takes the player out of the tree -- the
     button was left labelled "Pause" over a still frame with nothing playing.
 
     Keyed on `playing` rather than on `previewing`, because the same thing is true
     of any route to no-player: a card selection that resets state, or a committed
     video whose entry changes. */
  useEffect(() => {
    if (!playing) setRunning(false);
  }, [playing]);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Arriving on the page is silent (autoplay false); choosing any card sets it
  // true and every selection after that plays on arrival.
  useEffect(() => {
    setCommitted(autoplay);
    setPreviewing(false);
    // A fresh element starts paused, whatever the last one was doing.
    setRunning(false);
  }, [video.id, autoplay]);

  // `video.id` is load-bearing here, not decoration. Selecting a second card
  // while the first is playing leaves `playing` true — so keying this on
  // `playing` alone meant the effect never re-ran, while `key={video.id}` had
  // already remounted the element with a fresh, paused ref. The new video sat
  // on its poster underneath copy announcing it was now showing.
  useEffect(() => {
    /* `unmute` because this player is the one the viewer asked for -- by pressing
       Watch now, choosing a card, or hovering the stage. safePlay starts it muted so
       the browser allows the play at all, then clears `muted` once it is running;
       see that function for why the two-step is necessary. */
    if (playing) safePlay(videoRef.current, { unmute: true });

    /* Silence THIS element on the way out. React mounts the next entry's <video>
       before unmounting this one (they have different keys), so without an explicit
       stop the outgoing clip can still be playing -- audibly -- while the incoming
       one starts. Two soundtracks at once, for as long as the browser takes to tear
       the old node down. */
    const outgoing = videoRef.current;
    return () => {
      if (!outgoing) return;
      outgoing.muted = true;
      outgoing.pause();
    };
  }, [playing, video.id]);

  const runtime = formatRuntime(video.durationSeconds);
  const genre = video.tags?.[0];

  return (
    <section
      className="hero"
      aria-labelledby="hero-title"
      ref={anchorRef}
      /* Pointer events, not mouse events: these fire for pen and touch too, and
         `onPointerLeave` is what a touch device sends when the finger goes, so a
         tap-preview stops instead of running on invisibly. */
      onPointerEnter={previewOnHover ? () => setPreviewing(true) : undefined}
      onPointerLeave={
        previewOnHover
          ? () => {
              setPreviewing(false);
              /* Only when this was a preview: a committed video keeps playing on
                 mouse-out, and telling the caller "stopped" would restart its
                 idle timer while the video is still running. */
              if (!committed) onPause?.();
            }
          : undefined
      }
    >
      <div className="hero__stage">
        {playing ? (
          <video
            ref={videoRef}
            key={video.id}
            className="hero__video"
            src={video.videoUrl}
            poster={video.thumbnailUrl}
            /* Full controls either way in -- scrubber, time, volume, fullscreen.
               Hovering is a way of starting the same player, not a lesser preview
               mode, so the affordances should not differ by how playback began.
 
               No `muted` prop: the effect above owns it, because the value has to
               change BETWEEN the play() call and its resolution. React would
               re-assert whatever this said on the next render and re-mute a video
               that had just been unmuted. */
            controls
            /* `metadata`, matching BrowseCard and LandingStage. This element had no
               `preload` at all, so it defaulted to `auto` and the browser pulled the
               whole clip -- and the catalogue's videos are 200MB+, so "whole clip" is
               a quarter-gigabyte before the first frame.

               It only mounts once playback is asked for, so this is not about idle
               bandwidth: it is that `auto` lets the browser buffer far ahead of the
               playhead when what a viewer needs is the opening frames. `metadata`
               gets the dimensions and duration, then range-requests as it plays.

               Sizing the files is the real fix; this is the part the frontend owns. */
            preload="metadata"
            playsInline
            onEnded={onEnded}
            /* On the element rather than beside each caller: playback also starts
               from the controls' own play button, and a reel that only heard about
               Watch now would walk away from a video the viewer had just resumed. */
            onPlay={() => {
              setRunning(true);
              onPlay?.();
            }}
            onPause={() => {
              setRunning(false);
              /* The controls' pause counts too, not just ours. Guarded on the
                 element still existing and actually being paused, because a
                 remount on entry change fires this as it tears down. */
              if (videoRef.current?.paused) onPause?.();
            }}
          />
        ) : video.thumbnailUrl ? (
          <img className="hero__art" src={video.thumbnailUrl} alt="" aria-hidden />
        ) : null}

        {/* On the still as well as the player. A screen recording captures whatever
            is on the page, so a mark that only appeared during playback would leave
            the poster frame unattributed -- and the poster is what a viewer sees for
            most of the time they are looking at this. It cannot survive a download of
            the file itself, which is a limit of doing this in CSS rather than in the
            render. */}
        <span className="hero__watermark" aria-hidden>
          <ValoiV size={15} />
          Valoi
        </span>
      </div>

      <div className="hero__body">
        <p className="eyebrow">Now showing</p>
        <h2 className="hero__title" id="hero-title">
          {video.title}
        </h2>

        {runtime || genre ? (
          <p className="hero__meta">{[runtime, genre].filter(Boolean).join(' · ')}</p>
        ) : null}

        <p className="hero__description">{video.description}</p>

        <div className="hero__actions">
          {/* Labelled from `running` -- what the element is doing -- rather than
              from `committed`, the decision to show a player at all. Pausing with
              the video's own controls leaves the decision intact, so the old label
              still offered "Pause" over an already-paused frame: the one control on
              screen disagreeing with the picture beside it.
 
              Three states, because "paused but still here" is a real one and
              collapsing it into "Watch now" would restart from 0:00 and throw away
              the viewer's position. Resume plays the existing element instead of
              re-entering `committed`, which would re-mount it. */}
          <Button
            variant="inverse"
            iconLeft={running ? <PauseIcon size={15} /> : <PlayIcon size={15} />}
            onClick={() => {
              /* No onPause?.() on either branch: the element fires its own
                 play/pause, which already reports upward. Calling both sent the reel
                 two signals for one click. */
              if (running) {
                videoRef.current?.pause();
                return;
              }
              if (committed) {
                /* safePlay, not `play().catch()`: the spec only added the returned
                   promise later, so older engines and jsdom return `undefined` and
                   calling `.catch` on it throws. The helper normalises both -- and
                   is what the effect above already uses. */
                safePlay(videoRef.current);
                return;
              }
              setCommitted(true);
            }}
          >
            {running ? 'Pause' : committed ? 'Resume' : 'Watch now'}
          </Button>
          <Button
            variant="secondary"
            iconLeft={saved ? <BookmarkFilledIcon size={15} /> : <BookmarkIcon size={15} />}
            onClick={() => onToggleSave(video)}
          >
            {saved ? 'Added to library' : 'Add to library'}
          </Button>
        </div>
      </div>
    </section>
  );
}
